/* eslint-disable react/jsx-no-useless-fragment */
import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslator } from '@front-activerse/common-components';
import { Helmet } from 'react-helmet';
import { Col, Container, Row } from 'reactstrap';
import ExternalProviderContainer from '../../Auth/ExternalProvider/ExternalProviderContainer';
import { RETURN_URL_LOGIN } from '../../Auth/ExternalProvider/ReturnUrls';
import AuthLoginByEmailAndPasswordForm from './AuthLoginByEmailAndPasswordForm';
import Layout from '../../Layout/Layout';
import { currencySymbols } from '../const';

const AuthLoginByEmailAndPasswordContainer = ({
  login,
  isLoggedIn,
  userDataOnDemand,
  redirectToPathAfterSuccess,
  displayAppleButton,
  displayFacebookButton,
  displayGoogleButton,
  registerButtonURL,
  action,
  paymentMethods,
}) => {
  const navigate = useNavigate();

  const { t } = useTranslator();

  useEffect(() => {
    if (isLoggedIn) {
      navigate(redirectToPathAfterSuccess);
    }
  }, [isLoggedIn, navigate, redirectToPathAfterSuccess]);

  const products = JSON.parse(localStorage.getItem('shoppingCartProducts'));

  const totalPrice = {
    amount: products.reduce((acc, product) => acc + product.price.amount, 0),
    currency: products[0].price.currency,
  };

  return (
    <Layout simpleLayout>
      {!isLoggedIn && (
        <>
          <Helmet>
            <meta name="robots" content="noindex,nofollow" />
          </Helmet>
          <div className="page-template-auth full-height7">
            <Container className="py-xxl">
              <h1 className="text-center title-medium semi-bold mb-xl">{t('yourCart.title')}</h1>
              <Row>
                <Col xs={{ size: 12, order: 2 }} md={{ size: 6, order: 1 }}>
                  <div className="page-container">
                    <div className="auth-container">
                      <h1 className="text-center title-medium semi-bold mb-xl">
                        {t('logIn.title')}
                      </h1>
                      <ExternalProviderContainer
                        returnUrl={RETURN_URL_LOGIN}
                        displayAppleButton={displayAppleButton}
                        displayFacebookButton={displayFacebookButton}
                        displayGoogleButton={displayGoogleButton}
                        fullWidth
                        action={action}
                      />

                      {(displayAppleButton || displayFacebookButton || displayGoogleButton) && (
                        <div className="line">
                          <span>{t('authorization.continueWithEmail.description')}</span>
                        </div>
                      )}

                      <AuthLoginByEmailAndPasswordForm
                        login={login}
                        userDataOnDemand={userDataOnDemand}
                        redirectToPathAfterSuccess={redirectToPathAfterSuccess}
                      />
                    </div>
                  </div>
                  {registerButtonURL && (
                    <p className="mt-xl text-center">
                      {t('authorization.dontHaveAnAccount')}{' '}
                      <Link to={registerButtonURL}>{t('authorization.signUp.button')}</Link>
                    </p>
                  )}
                </Col>
                <Col xs={{ size: 12, order: 1 }} md={{ size: 6, order: 2 }}>
                  <div className="page-container">
                    <div className="auth-container">
                      <h3 className="title-medium semi-bold mb-xl">
                        {t('paymentMethods.summary.title')}
                      </h3>
                      <div>
                        {products.map((product) => (
                          <div className="d-flex justify-content-between" key={product.id}>
                            <p
                              className="mb-0 pe-4"
                              dangerouslySetInnerHTML={{
                                __html:
                                  product.saleProductVariant.saleProduct.productCardTitle || '',
                              }}
                            />
                            <p className="mb-0 text-nowrap">
                              {product.price.amount} {currencySymbols[product.price.currency]}
                            </p>
                          </div>
                        ))}

                        <hr />
                        <div className="d-flex justify-content-between">
                          <h3 className="title-medium semi-bold mb-0">
                            {t('paymentMethods.summary.total.title')}
                          </h3>
                          <h3 className="title-medium semi-bold mb-0">
                            {totalPrice.amount + ' ' + currencySymbols[totalPrice.currency]}
                          </h3>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="px-3 mb-xl">
                    <p className="semi-bold mt-xl">{t('acceptedPaymentMethods.title')}</p>
                    {paymentMethods?.map((paymentMethod) => (
                      <img
                        className="me-m mb-m"
                        src={paymentMethod.logoUrl}
                        alt={paymentMethod.name}
                        key={paymentMethod.id}
                      />
                    ))}
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </>
      )}
    </Layout>
  );
};

AuthLoginByEmailAndPasswordContainer.propTypes = {
  login: PropTypes.func.isRequired,
  isLoggedIn: PropTypes.bool.isRequired,
  userDataOnDemand: PropTypes.func.isRequired,
  redirectToPathAfterSuccess: PropTypes.string.isRequired,
};

export default AuthLoginByEmailAndPasswordContainer;
